import { Controller } from '@hotwired/stimulus'
import {
  readMarkdownFromEditor,
  writeMarkdownToEditor,
} from './helpers/lexxyEditorMarkdown'

export default class DraftContentController extends Controller {
  #previewSections = new Map() // title -> { editor, text, previewLi }
  #previewHeader = null

  async getContent() {
    const sectionsData = await this.#extractSections()
    return this.#format(sectionsData)
  }

  async updateContent(newContent) {
    this.#removeAllPreviews()

    const parser = new DOMParser()
    const parsedDocument = parser.parseFromString(
      newContent,
      'application/xml',
    )

    const sections = Array.from(parsedDocument.querySelectorAll('section'))

    for (const section of sections) {
      const title = section.title
      const sectionToFind = this.#findSectionForTitle(title)
      if (!sectionToFind) continue
      const textNodes = Array.from(section.childNodes).filter(
        child => child.nodeType === Node.TEXT_NODE,
      )
      const text = textNodes.map(node => node.textContent).join('\n')
      this.#insertPreviewForSection(title, sectionToFind.editor, text)
    }

    if (this.#previewSections.size > 0) {
      this.#insertHeader()
    }
  }

  showPreview() {
    this.#showPreview()
  }

  async acceptSection(title) {
    const entry = this.#previewSections.get(title)
    if (!entry) return
    const { editor, text } = entry
    await writeMarkdownToEditor(editor, text)
    entry.previewLi.remove()
    this.#previewSections.delete(title)
    this.#maybeHideHeader()
  }

  rejectSection(title) {
    const entry = this.#previewSections.get(title)
    if (!entry) return
    entry.previewLi.remove()
    this.#previewSections.delete(title)
    this.#maybeHideHeader()
  }

  async acceptAll() {
    for (const [, entry] of this.#previewSections.entries()) {
      await writeMarkdownToEditor(entry.editor, entry.text)
    }
    this.#removeAllPreviews()
  }

  rejectAll() {
    this.#removeAllPreviews()
  }

  #showPreview() {
    const hasPreview = this.#previewSections.size > 0
    if (this.#previewHeader) {
      this.#previewHeader.style.display = hasPreview ? '' : 'none'
    }
  }

  #insertHeader() {
    const acceptAllBtn = document.createElement('button')
    acceptAllBtn.className = 'btn btn--primary'
    acceptAllBtn.textContent = 'Accept All'
    acceptAllBtn.addEventListener('click', () => this.acceptAll())

    const rejectAllBtn = document.createElement('button')
    rejectAllBtn.className = 'btn'
    rejectAllBtn.textContent = 'Reject All'
    rejectAllBtn.addEventListener('click', () => this.rejectAll())

    this.#previewHeader = document.createElement('div')
    this.#previewHeader.className = 'ai-preview-header'
    this.#previewHeader.append(
      document.createTextNode('AI-Generated Preview'),
      document.createElement('br'),
      acceptAllBtn,
      document.createTextNode(' '),
      rejectAllBtn,
    )
    this.element.prepend(this.#previewHeader)
  }

  #maybeHideHeader() {
    if (this.#previewSections.size === 0 && this.#previewHeader) {
      this.#previewHeader.remove()
      this.#previewHeader = null
    }
  }

  #insertPreviewForSection(title, editor, text) {
    const lexxyEditor = editor.getRootElement().closest('lexxy-editor')
    if (!lexxyEditor) return

    const previewLi = document.createElement('li')
    previewLi.className = 'preview-section ai-preview-section'

    const previewBlock = document.createElement('div')
    previewBlock.className = 'ai-preview-block'

    const textNode = document.createTextNode(text)
    previewBlock.append(textNode)

    const acceptBtn = document.createElement('button')
    acceptBtn.className = 'btn btn--primary btn--small'
    acceptBtn.textContent = 'Accept'
    acceptBtn.addEventListener('click', () => this.acceptSection(title))

    const rejectBtn = document.createElement('button')
    rejectBtn.className = 'btn btn--small'
    rejectBtn.textContent = 'Reject'
    rejectBtn.addEventListener('click', () => this.rejectSection(title))

    const controls = document.createElement('div')
    controls.className = 'ai-preview-controls'
    controls.append(acceptBtn, rejectBtn)
    previewBlock.append(controls)
    previewLi.append(previewBlock)

    lexxyEditor.after(previewLi)
    this.#previewSections.set(title, { editor, text, previewLi })
  }

  #removeAllPreviews() {
    for (const { previewLi } of this.#previewSections.values()) {
      previewLi.remove()
    }
    this.#previewSections.clear()
    if (this.#previewHeader) {
      this.#previewHeader.remove()
      this.#previewHeader = null
    }
  }

  #findSectionForTitle(title) {
    const titleInput = Array.from(
      this.element.querySelectorAll(`input[name="section[title]"]`),
    ).find(input => input.value === title)
    const parentForm = titleInput.closest('form')
    const contentElement = parentForm.querySelector(
      '[name="section[content]"]',
    )
    return contentElement
  }

  async #extractSections(
    sectionsRoot = document.querySelector('#meeting_sections'),
  ) {
    const subsections = Array.from(sectionsRoot.querySelectorAll('& > li'))
    return await Promise.all(
      subsections.map(subsection => this.#extractSubsection(subsection)),
    )
  }

  async #extractSubsection(sectionRoot) {
    const sectionForm = sectionRoot.querySelector('& > turbo-frame > form')
    const formElements = Array.from(sectionForm.elements)
    const titleElement = formElements.find(
      el => el.name === 'section[title]',
    )
    const contentsElement = formElements.find(
      el => el.name === 'section[content]',
    )

    const title = titleElement.value
    const markdownContents = await readMarkdownFromEditor(
      contentsElement.editor,
    )

    const subsectionsRoot = sectionRoot.querySelector(
      `#${sectionRoot.id}_subsections`,
    )
    const subsections = await this.#extractSections(subsectionsRoot)

    return {
      title,
      content: markdownContents,
      subsections,
    }
  }

  #format(sections) {
    const root = document.createElement('meeting')
    this.#addSectionsToRoot(sections, root)
    const serializer = new XMLSerializer()
    return serializer.serializeToString(root)
  }

  #addSectionsToRoot(sections, root) {
    for (const section of sections) {
      const sectionRoot = document.createElement('section')
      root.append(sectionRoot)

      sectionRoot.title = section.title
      sectionRoot.textContent = section.content

      if (section.subsections.length > 0) {
        this.#addSectionsToRoot(section.subsections, sectionRoot)
      }
    }
  }
}
